import React, { useEffect, useRef, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import client from "@/lib/api";

const STORAGE_KEY = "exitIntentSeen_v1";
const PROMO_KEY = "exitIntentCode_v1";

/**
 * Triggers once per browser when the mouse leaves the top of the viewport.
 * Captures an email → mints a fresh single-use 10% code via the existing
 * personal-promo style endpoint (a new lightweight public endpoint).
 *
 * We never re-show after dismissal in the same browser. Codes are minted
 * server-side; the email becomes a marketing subscriber automatically.
 */
export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [code, setCode] = useState("");
  const armedRef = useRef(false);
  const dismissedRef = useRef(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
      const existing = localStorage.getItem(PROMO_KEY);
      if (existing) {
        // Already captured in a previous visit — don't pester
        return;
      }
    } catch { /* ignore */ }

    const armTimer = setTimeout(() => { armedRef.current = true; }, 8000); // arm after 8s

    const handler = (e) => {
      if (!armedRef.current || dismissedRef.current) return;
      // Trigger when pointer leaves through the top edge
      if (e.clientY <= 0) {
        dismissedRef.current = true;
        setOpen(true);
        try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
      }
    };
    document.addEventListener("mouseout", handler);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", handler);
    };
  }, []);

  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending" || status === "done") return;
    if (!email || !email.includes("@")) {
      setStatus("error");
      return;
    }
    setStatus("sending");
    try {
      const r = await client.post("/marketing/exit-intent", { email });
      setCode((r.data && r.data.code) || "MOTOSTAY10");
      setStatus("done");
      try { localStorage.setItem(PROMO_KEY, r.data?.code || "MOTOSTAY10"); } catch { /* ignore */ }
    } catch {
      // Even if the server rejects, give the user a safe fallback so they
      // don't leave with a broken impression.
      setCode("MOTOSTAY10");
      setStatus("done");
      try { localStorage.setItem(PROMO_KEY, "MOTOSTAY10"); } catch { /* ignore */ }
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={close}
      data-testid="exit-intent-popup"
    >
      <div
        className="relative bg-white max-w-[460px] w-full p-8 md:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Close"
          onClick={close}
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-black/50 hover:text-black"
          data-testid="exit-intent-close"
        >
          <X size={18} />
        </button>
        {status === "done" ? (
          <div className="text-center" data-testid="exit-intent-done">
            <Sparkles size={28} className="mx-auto text-[#FF3B30]" />
            <h2 className="font-display text-3xl mt-4 tracking-tighter">You're in.</h2>
            <p className="text-sm text-black/60 mt-3">Use this code at checkout. Single use.</p>
            <div className="my-6 inline-block bg-black text-white px-6 py-3 font-display text-2xl tracking-[0.28em]" data-testid="exit-intent-code">{code}</div>
            <div className="text-xs text-black/50">10% off · expires in 14 days · cannot stack with bundles or sales.</div>
            <Link
              to="/shop"
              onClick={close}
              className="inline-block mt-6 bg-[#FF3B30] text-white px-6 py-3 label-eyebrow hover:bg-black"
              data-testid="exit-intent-shop"
            >
              Use it on the drop
            </Link>
          </div>
        ) : (
          <>
            <div className="label-eyebrow text-[#FF3B30]">Before you go</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tighter">Take 10% off your first order.</h2>
            <p className="text-sm text-black/60 mt-3 leading-relaxed">
              Drop your email and we'll send a single-use code. We send a few signals a month — sales, new drops, the occasional studio story. Nothing else.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="exit-intent-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full border-b border-black bg-transparent py-3 focus:outline-none text-base"
                data-testid="exit-intent-email"
              />
              {status === "error" && (
                <div className="text-xs text-[#FF3B30]" data-testid="exit-intent-error">Please enter a valid email.</div>
              )}
              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full bg-black text-white px-6 py-3 label-eyebrow hover:bg-[#FF3B30] disabled:opacity-50"
                data-testid="exit-intent-submit"
              >
                {status === "sending" ? "Sending…" : "Send my 10% code"}
              </button>
              <button
                type="button"
                onClick={close}
                className="block w-full text-center text-xs text-black/40 underline mt-2"
                data-testid="exit-intent-decline"
              >
                No thanks, full price is fine
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
